import { IAggFunc, IAggFuncParams, RowClassParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { merge } from 'lodash-es';
import * as yup from 'yup';
import { createCompanyDataModel, ICompanyDataModel } from '../../../data-models/company.data-model';
import { IRoundDataModel } from '../../../data-models/round.data-model';
import { FundReserves, InvestmentType } from '../../../schemas/FundReserves.schema';
import { usdField } from '../../../schemas/common-schema-defs';
import { roundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { portcosByIdMapAtomJ } from '../../../services/state/PortCosStateJ';
import { sumOrNull } from '../../../util/math-utils';
import { uuid } from '../../../util/uuid';
import { FDIterator } from '../../PortfolioOverview/components/OverviewTable/StandardIterators';
import { runAggregationIterator } from '../../PortfolioOverview/components/OverviewTable/columns/AggregationConfigs';
import { FundCompanyCellRenderer } from './reservesGridColDefs';

/*
- All investment amounts except the initial one will count towards reserves used (current ones will count towards reserves invested, future ones towards reserves assigned)
*/

function reservesAggregationFields() {
  return {
    reservesInvested: usdField().nullable().label('Reserves Invested'),
    reservesAssigned: usdField().nullable().label('Reserves Assigned'),
    value: usdField().nullable().label('Reserves Remaining'),
  };
}

function remainingReservesSchema() {
  return yup
    .object({
      ...reservesAggregationFields(),
    })
    .nullable()
    .gridMeta({
      formatter: 'currency0Dp',
      aggFunc: 'remainingReservesAggFunc',
    });
}

function projectedReservesSchema() {
  return yup
    .object({
      ...reservesAggregationFields(),
      value: usdField().nullable().label('Projected Reserves'),
    })
    .nullable()
    .gridMeta({
      aggFunc: 'projectedReservesAggFunc',
      formatter: 'currency0Dp',
    });
}

function showValue(level?: number) {
  return typeof level === 'number' && level < 1;
}

type ReservesAggResult = {
  reservesAssigned: number | null;
  reservesInvested: number | null;
  value: number | null;
};

function remainingReservesIterator(): FDIterator<ReservesAggResult, ReservesAggResult, IAggFuncParams> {
  let reservesAssigned: number | null = null;
  let reservesInvested: number | null = null;
  let value: number | null = null;

  return {
    get value() {
      return {
        reservesInvested,
        reservesAssigned,
        value,
      };
    },

    next(data, params) {
      if (data) {
        reservesAssigned = sumOrNull(reservesAssigned, data.reservesAssigned);
        reservesInvested = sumOrNull(reservesInvested, data.reservesInvested);
      }
      value = showValue(params?.rowNode?.level) ? (reservesAssigned ?? 0) - (reservesInvested ?? 0) : null;
    },
  };
}

function projectedReservesIterator(): FDIterator<ReservesAggResult, ReservesAggResult, IAggFuncParams> {
  let reservesAssigned: number | null = null;
  let reservesInvested: number | null = null;
  let value: number | null = null;

  return {
    get value() {
      return {
        reservesInvested,
        reservesAssigned,
        value,
      };
    },
    next(data, params) {
      if (data) {
        reservesAssigned = sumOrNull(reservesAssigned, data.reservesAssigned);
        reservesInvested = sumOrNull(reservesInvested, data.reservesInvested);
      }
      value =
        !showValue(params?.rowNode?.level) || (reservesAssigned == null && reservesInvested == null)
          ? null
          : Math.max(reservesAssigned ?? 0, reservesInvested ?? 0);
    },
  };
}

type TotalProjectedInvestmentAggResult = {
  initialInvestment: number | null;
  reservesInvested: number | null;
  reservesAssigned: number | null;
};
const totalProjectedInvestmentIterator = (): FDIterator<
  TotalProjectedInvestmentAggResult & { value: number | null },
  TotalProjectedInvestmentAggResult
> => {
  let initialInvestment: number | null = null;
  let reservesInvested: number | null = null;
  let reservesAssigned: number | null = null;

  return {
    get value() {
      const value = sumOrNull(
        initialInvestment,
        reservesInvested == null && reservesAssigned == null
          ? null
          : Math.max(reservesInvested ?? 0, reservesAssigned ?? 0)
      );
      return {
        initialInvestment,
        reservesInvested,
        reservesAssigned,
        value,
      };
    },
    next(data) {
      if (data) {
        initialInvestment = sumOrNull(initialInvestment, data.initialInvestment);
        reservesInvested = sumOrNull(reservesInvested, data.reservesInvested);
        reservesAssigned = sumOrNull(reservesAssigned, data.reservesAssigned);
      }
    },
  };
};

// define separate iterator runner for reserves in order to keep runAggregationIterator context type generic
function runReservesAggregationIterator(
  params: IAggFuncParams,
  iterator: FDIterator<ReservesAggResult, ReservesAggResult, IAggFuncParams>
) {
  if (params.rowNode.leafGroup) {
    params.rowNode.childrenAfterFilter?.forEach((node) => {
      iterator.next(node.data, params);
    });
  } else {
    params.values.forEach((val) => {
      iterator.next(val, params);
    });
  }

  return iterator.value;
}

export const reservesAggFuncs: Record<string, IAggFunc<ReservesRowData>> = {
  remainingReservesAggFunc: (params: IAggFuncParams) =>
    runReservesAggregationIterator(params, remainingReservesIterator()),
  projectedReservesAggFunc: (params: IAggFuncParams) =>
    runReservesAggregationIterator(params, projectedReservesIterator()),
  totalProjectedInvestmentAggFunc: (params: IAggFuncParams) =>
    runAggregationIterator(params, totalProjectedInvestmentIterator()),
};

export function ReservesRowDataFields() {
  return {
    company: yup
      .object()
      .gridMeta({
        cellRenderer: FundCompanyCellRenderer,
      })
      .label('Company'),
    companyId: yup.number().nullable().label('Company'),
    companyName: yup.string().nullable().label('Company'),
    date: yup.date(),
    description: yup.string().nullable().label('Description'),
    hypothetical: yup.boolean().nullable().gridMeta({
      hide: true,
    }),
    initialInvestment: usdField().nullable().gridMeta({
      formatter: 'currency0Dp',
      aggFunc: 'sum',
    }),
    investmentAmount: usdField().nullable().label('Total Projected Investment').gridMeta({
      formatter: 'currency0Dp',
      aggFunc: 'totalProjectedInvestmentAggFunc',
    }),
    investmentId: yup.number().nullable(),
    projectedReserves: projectedReservesSchema(),
    reservesInvested: usdField().nullable().gridMeta({
      formatter: 'currency0Dp',
      aggFunc: 'sum',
    }),
    reservesAssigned: usdField().nullable().label('Assigned Reserves').gridMeta({
      formatter: 'currency0Dp',
      aggFunc: 'sum',
    }),
    reservesRemaining: remainingReservesSchema(),
    sectorId: yup.number().label('Sector').gridMeta({ formatter: 'sector' }),
    transactionGroupName: yup.string().nullable().label('Transaction Group'),
    type: yup.string().nullable().label('Investment'),
    uuid: yup.string().nullable(),
  };
}
export function ReservesRowDataSchema() {
  return yup.object().shape(ReservesRowDataFields());
}
export type ReservesRowData = yup.InferType<ReturnType<typeof ReservesRowDataSchema>> & {
  company: Omit<ICompanyDataModel, 'id'>;
};

export function createReservesRowData(overrides: Partial<ReservesRowData> = {}): ReservesRowData {
  return merge({}, ReservesRowDataSchema().getDefault(), overrides);
}

export type ReservesDraftRow = ReservesRowData & { isDraft: true };

export function isReservesDraftRow(data?: ReservesRowData | null): data is ReservesDraftRow {
  return (data as ReservesDraftRow | undefined)?.isDraft === true;
}

interface ICreateReservesDraftRowParams {
  sourceRow: ReservesRowData;
  type: InvestmentType;
}

export function createReservesDraftRow({ sourceRow, type }: ICreateReservesDraftRowParams): ReservesDraftRow {
  return {
    ...createReservesRowData({
      company: sourceRow.company,
      companyId: sourceRow.companyId,
      companyName: sourceRow.companyName,
      description: sourceRow.description,
      hypothetical: true,
      investmentId: sourceRow.investmentId,
      sectorId: sourceRow.sectorId,
      transactionGroupName: 'Hypothetical',
      type,
      // client-only id so getRowId can track the draft; the BE mints real uuids
      uuid: `draft-${uuid()}`,
    }),
    isDraft: true,
  };
}

export function useReservesGridData(reserves: FundReserves) {
  const portcos = useAtomValue(portcosByIdMapAtomJ);
  const roundMap = useAtomValue(roundsByIdMapAtom);
  return getReservesGridData(reserves, portcos, roundMap);
}

export function getReservesGridData(
  reserves: FundReserves,
  portcosMap: Map<number, ICompanyDataModel>,
  roundMap: Map<number, IRoundDataModel>
) {
  const data: ReservesRowData[] = [];

  (reserves.investments ?? []).forEach((investment) => {
    const { companyId, name: companyName, description, sectorId } = investment;

    const company =
      portcosMap.get(companyId ?? -1) ??
      createCompanyDataModel({ id: -1, name: companyName ?? 'Unknown Company', description, sectorId });

    investment.transactions.forEach((transaction) => {
      const { hypothetical, uuid, type } = transaction;
      const reservesAssigned = hypothetical && type !== InvestmentType.Initial ? transaction.amount : null; // sumByProperty('amount', futureInvestment.transactions)
      const reservesInvested =
        !hypothetical && transaction.type === InvestmentType.FollowOn ? transaction.amount : null; // fundMetrics.metrics.followOnInvestmentAmount
      const initialInvestment = transaction.type === InvestmentType.Initial ? transaction.amount : null;
      data.push({
        company,
        companyId: companyId ?? null,
        companyName: companyId ? null : companyName,
        date: transaction.date as Date,
        description: company.description,
        hypothetical,
        investmentAmount: transaction.amount,
        investmentId: investment.id,
        initialInvestment,
        reservesAssigned,
        reservesInvested,
        projectedReserves: {
          reservesAssigned,
          reservesInvested,
          value: null, // only show at aggregate level
        },
        reservesRemaining: {
          reservesAssigned,
          reservesInvested,
          value: null, // only show at aggregate level
        },
        sectorId: company.sectorId ?? undefined,
        transactionGroupName: hypothetical
          ? 'Hypothetical'
          : (roundMap.get(transaction.roundId ?? -1)?.name ?? 'N/A'),
        type: transaction.type,
        uuid,
      });
    });
  });
  return data;
}

export const reservesRowClassRules = {
  currentInvestment: (params: RowClassParams) => {
    return params.data?.hypothetical === false;
  },
  'reserves-draft-row': (params: RowClassParams) => {
    return isReservesDraftRow(params.data);
  },
};
